import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import React from 'react';
import { LookBackYearRange } from '../../graphQL/types/shared';
import PresentationManager from '../../presentationManager/PresentationManager';
import { VizType } from '../../viz/Utils';
import ProductSpace from '../countryProductSpaceChart';
import CountrySelector from '../countrySelector/InteriorCountrySelector';
import ExploreEngageButtons from '../exploreEngageButtons';
import { TradeFlowSelectorContainer as NodeSizingContainer} from '../exportBasket';
import LeftPanelBoxShadow from '../exportBasket/LeftPanelBoxShadow';
import GraphControlDropdown, {
  IOption,
} from '../graphControlDropdown';
import {
  Root,
} from '../Grid';
import HamburgerMenu from '../menu';
import SectionNav from '../sectionNav';
import SectorLabels from '../SectorLabels';
import Sidebar from '../sidebar';
import UtilityBar from '../utilityBarItems';
import {chapters} from '../utilityBarItems/VideoTutorial';
import {
  CommonElementName,
  defaultFadeTransition,
  defaultVerticalSlideTransition,
  ExportsChangeNodeSizing,
  getUniqueSlideElementKey,
  SlideName,
} from '../Utils';
import TopOfPageIndicators from './TopOfPageIndicators';

const getProductSpaceNodeSizingOptions =
  (getFluentString: GetString): Array<IOption<ExportsChangeNodeSizing>> => ([
    {
      value: ExportsChangeNodeSizing.WorldTrade,
      label: getFluentString('growth-dynamics-node-sizing-world-trade'),
      isEnabled: true,
    },
    {
      value: ExportsChangeNodeSizing.CountryTrade,
      label: getFluentString('growth-dynamics-node-sizing-country-trade'),
      isEnabled: true,
    },
    {
      value: ExportsChangeNodeSizing.None,
      label: getFluentString('growth-dynamics-node-sizing-none'),
      isEnabled: true,
    },
  ]);

interface Input {
  getFluentString: GetString;
  countryId: string;
  year: number;
  selectCountry: (country: string) => void;
  slideIndex: number;
  firstContentfulSlideIndex: number;
  setSlideIndex: (index: number) => void;
  goToNextSlide: () => void;
  goToPrevSlide: () => void;
  fluentBundle: FluentBundle;
  sidebarZIndex: number;
  chartZIndex: number;
  countrySelectorZIndex: number;
  sectionNavZIndex: number;
  hamburgerMenuZIndex: number;
  topOfPageIndicatorsZIndex: number;
  nodeSizingDropdownZIndex: number;
  exploreEngageButtonsZIndex: number;
  productSpaceNodeSizing: ExportsChangeNodeSizing;
  setProductSpaceNodeSizing: (value: ExportsChangeNodeSizing) => void;
  sectorLabelsZIndex: number;
  helpTipsTriggerZIndex: number;
  toggleHelpTooltips: () => void;
  helpTooltipsActive: boolean;
  promotedZIndex: number;
}

const getCountryProductSpaceSlide = (input: Input) => {
  const {
    countryId, year, selectCountry,
    firstContentfulSlideIndex,
    setSlideIndex, slideIndex, goToNextSlide, fluentBundle,
    sidebarZIndex, chartZIndex, countrySelectorZIndex,
    sectionNavZIndex, hamburgerMenuZIndex, getFluentString,
    topOfPageIndicatorsZIndex, exploreEngageButtonsZIndex,
    productSpaceNodeSizing, setProductSpaceNodeSizing,
    nodeSizingDropdownZIndex, sectorLabelsZIndex, goToPrevSlide,
    helpTipsTriggerZIndex, toggleHelpTooltips, helpTooltipsActive,
    promotedZIndex,
  } = input;

  const helpTooltipOrder = {
    dropdown: 1,
    sectionNav: 2,
    pageAdvancer: 3,
    exploreButton: 4,
    sectorLabels: 5,
  };

  return (
    <PresentationManager RootComponent={Root} slideIndex={slideIndex}>
      <CountrySelector
        selectedCountry={countryId}
        onCountrySelect={selectCountry}
        promptText={getFluentString('country-selector-prompt')}
        getFluentString={getFluentString}
        zIndex={countrySelectorZIndex}
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.CountrySelector}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.dropdown}
      />
      <SectionNav
        startingIndexOfFirstSection={0}
        slideIndex={slideIndex}
        setSlideIndex={setSlideIndex}
        year={year}
        getFluentString={getFluentString}
        countryId={countryId}
        zIndex={sectionNavZIndex}
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.SectionNav}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.sectionNav}
      />
      <Sidebar
        firstContentfulSlideIndex={firstContentfulSlideIndex}
        slideIndex={slideIndex}
        getFluentString={getFluentString}
        goToNextSlide={goToNextSlide}
        goToPrevSlide={goToPrevSlide}
        countryId={countryId}
        fluentBundle={fluentBundle}
        yearRange={LookBackYearRange.FifteenYears}
        zIndex={sidebarZIndex}
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.Sidebar}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.pageAdvancer}
      />
      <LeftPanelBoxShadow
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.LeftPanelBackground}
      />
      <ProductSpace
        year={year}
        zIndexFromAbove={chartZIndex}
        countryId={countryId}
        getFluentString={getFluentString}
        showAllProducts={false}
        showTitle={true}
        transition={defaultFadeTransition}
        showSectorLabels={false}
        nodeSizing={productSpaceNodeSizing}
        key={getUniqueSlideElementKey(SlideName.CountryProductSpace, 'product-space-chart')}
      />
      <SectorLabels
        isServicesEnabled={false}
        zIndex={sectorLabelsZIndex}
        countryId={countryId}
        hideOnTechFrontier={false}
        transition={defaultFadeTransition}
        getFluentString={getFluentString}
        key={CommonElementName.SectorLabels}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.sectorLabels}
      />
      <TopOfPageIndicators
        countryId={countryId}
        year={year}
        getFluentString={getFluentString}
        zIndex={topOfPageIndicatorsZIndex}
        transition={defaultFadeTransition}
        key={getUniqueSlideElementKey(SlideName.ExportBasket, 'top-page-indicators')}
      />
      <GraphControlDropdown
        label={getFluentString('country-product-space-node-sizing-label')}
        selectedValue={productSpaceNodeSizing}
        options={getProductSpaceNodeSizingOptions(getFluentString)}
        onValueSelect={setProductSpaceNodeSizing}
        zIndex={nodeSizingDropdownZIndex}
        Container={NodeSizingContainer}
        promotedZIndex={promotedZIndex}
        transition={defaultFadeTransition}
        key={getUniqueSlideElementKey(SlideName.ExportGrowthDynamics, 'node-sizing-selector')}
      />
      <ExploreEngageButtons
        zIndex={exploreEngageButtonsZIndex}
        year={year}
        countryId={countryId}
        vizType={VizType.Network}
        getFluentString={getFluentString}
        transition={defaultFadeTransition}
        key={CommonElementName.ExploreEngageButtons}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.exploreButton}
      />
      <HamburgerMenu
        getFluentString={getFluentString}
        zIndex={hamburgerMenuZIndex}
        transition={defaultFadeTransition}
        key={CommonElementName.HamburgerMenu}
      />
      <UtilityBar
        getFluentString={getFluentString}
        zIndex={helpTipsTriggerZIndex}
        toggleHelpTooltips={toggleHelpTooltips}
        helpTooltipsActive={helpTooltipsActive}
        transition={defaultFadeTransition}
        countryId={countryId}
        videoChapter={chapters[7]}
        key={CommonElementName.HelpTipsTrigger}
      />
    </PresentationManager>
  );
};

export default getCountryProductSpaceSlide;
